import {
  Button,
  Chip,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CustomDetailsList,
  CustomTabPanel,
  Loader,
  PageListLayout,
  usePermission,
} from "../../../shared";
import { useStockItemDetails } from "../hooks";
import { useNavigate, useParams } from "react-router-dom";
import { StoreStocksSettings } from "../components";
import { formatCost } from "../../../utils";

export const StockItemDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isIngredientEditable = usePermission("ingredient-modify");
  const isRecipeEditable = usePermission("recipe-stock-take-view");

  const { handleChange, search, stockItemDetails, isLoading } =
    useStockItemDetails();

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/stock-items")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Stock Items
    </Link>,
    <Typography
      key="2"
      sx={{ fontSize: "14px", fontWeight: 400, color: "text.secondary" }}
    >
      {isLoading ? "...Loading" : stockItemDetails?.name}
    </Typography>,
  ];

  const columns = [
    {
      title: "Name",
      accessor: (val) => val.name || "-",
    },
    {
      title: "Stocktake Status",
      accessor: (val) => (
        <Chip
          size="small"
          label={val.status ? "ENABLED" : "DISABLED"}
          variant={val.status ? "generated" : "stockTakeDraft"}
        />
      ),
    },
    {
      title: "Category",
      accessor: (val) => val.category || "-",
    },
    {
      title: "Sub Category",
      accessor: (val) => val.subcategory || "-",
    },
    {
      title: "Stock Code",
      accessor: (val) => val.stock_code || "-",
    },
    {
      title: "Stock Unit",
      accessor: (val) => val.unit || "-",
    },
    {
      title: "Cost(Ex GST)/unit",
      accessor: (val) =>
        val.total_cost_exc_gst ? `$${formatCost(val.total_cost_exc_gst)}` : "-",
    },
  ];

  return (
    <PageListLayout
      headerText={
        isLoading ? (
          "...Loading"
        ) : (
          <Stack direction="row" alignItems="center" gap="8px">
            <Typography sx={{ fontWeight: 600, fontSize: "24px" }}>
              {stockItemDetails?.name}
            </Typography>
            {stockItemDetails?.type_status ? (
              <Chip
                size="small"
                variant="filled"
                label="ACTIVE"
                color="primary"
              />
            ) : (
              <Chip
                size="small"
                variant="filled"
                label="INACTIVE"
                color="gray"
              />
            )}
          </Stack>
        )
      }
      breadcrumbs={breadcrumbs}
      tabs={
        <Tabs value={search} onChange={handleChange}>
          <Tab label="Basic Details" value="?basic-details" />
          <Tab label="Store Settings" value="?store-settings" />
        </Tabs>
      }
      sx={{
        backgroundColor: "background.paper",
        minHeight: "calc(100vh - 145px)",
      }}
    >
      <CustomTabPanel value={search} index="?basic-details" border={false}>
        {isLoading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "calc(100vh - 300px)" }}
          >
            <Loader />
          </Stack>
        ) : (
          <CustomDetailsList
            columns={columns}
            data={stockItemDetails || {}}
            isEmpty={!isLoading && !stockItemDetails}
            title="Basic Details"
            headerRightContent={
              isIngredientEditable || isRecipeEditable ? (
                <Button
                  variant="outlined"
                  color="gray2"
                  sx={{ maxWidth: "78px" }}
                  disabled={!stockItemDetails}
                  onClick={() => {
                    if (stockItemDetails) {
                      const isIngredient =
                        stockItemDetails.type === "ingredient";
                      const redirectUrl = isIngredient
                        ? `/ingredients/details/${stockItemDetails.type_id}?stock-take`
                        : `/recipe/details/${stockItemDetails.type_id}?stock-items=&modal=add-stock-item&stockId=${id}`;
                      const enableRecipe = isRecipeEditable && !isIngredient;
                      const enableIngredient =
                        isIngredientEditable && isIngredient;
                      const enableEdit = enableRecipe || enableIngredient;
                      navigate(enableEdit ? redirectUrl : null);
                    }
                  }}
                >
                  Edit
                </Button>
              ) : null
            }
          />
        )}
      </CustomTabPanel>
      <CustomTabPanel value={search} index="?store-settings" border={false}>
        <StoreStocksSettings />
      </CustomTabPanel>
    </PageListLayout>
  );
};
